import React, { useState } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Select from "../../../components/ui/Select";
import OrderStatusBadge from "./OrderStatusBadge";
import { useTranslation } from "react-i18next";
import {
  getOrdersService,
  updateOrderStatusService,
} from "redux/services/orders_service";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";

const OrderDetailsModal = ({ order, onClose }) => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState("details");
  const [newStatus, setNewStatus] = useState(order.status);

  const statusOptions = [
    { value: "not_confirmed", label: t("not_confirmed") },
    { value: "in_progress", label: t("processing") },
    { value: "shipped", label: t("shipped") },
    { value: "delivered", label: t("delivered") },
    { value: "canceled", label: t("cancelled") },
  ];

  const tabs = [
    { id: "details", label: t("orderDetails"), icon: "FileText" },
    { id: "items", label: t("orderItems"), icon: "Package" },
    { id: "shipping", label: t("shippingAddress"), icon: "Truck" },
    { id: "payment", label: t("paymentDetails"), icon: "CreditCard" },
  ];

  const formatPrice = (price) =>
    new Intl.NumberFormat(i18n.language === "en" ? "en" : "ar", {
      style: "currency",
      currency: "IQD",
      maximumFractionDigits: 0,
    }).format(price);

  const formatDate = (date) =>
    new Date(date).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  const handleStatusUpdate = async () => {
    try {
      await dispatch(
        updateOrderStatusService({
          id: order.id,
          data: { status: newStatus },
        }),
      ).then((res) => {
        if (res?.payload) {
          toast("تم تغيير الحالة بنجاح");
          onClose();
          dispatch(getOrdersService());
        }
      });
    } catch (err) {
      console.error(err);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "details":
        return (
          <div className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div>
                  <label className="text-sm font-semibold text-gray-500">
                    {t("orderNumber")}
                  </label>
                  <div className="font-mono text-xl font-bold">
                    #{order.order_number}
                  </div>
                </div>

                <div>
                  <label className="text-sm font-semibold text-gray-500">
                    {t("order_date")}
                  </label>
                  <div>{formatDate(order.created_at)}</div>
                </div>

                <div>
                  <label className="text-sm font-semibold text-gray-500">
                    {t("status")}
                  </label>
                  <div className="mt-2">
                    <OrderStatusBadge status={order.status} />
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="text-sm font-semibold text-gray-500">
                    {t("customer")}
                  </label>
                  <div className="font-semibold">
                    {order.user.first_name} {order.user.last_name}
                  </div>
                  <div className="text-sm text-gray-500">
                    {order.user.email}
                  </div>
                </div>

                <div>
                  <label className="text-sm font-semibold text-gray-500">
                    {t("total")}
                  </label>
                  <div className="text-2xl font-bold">
                    {formatPrice(order.total_price)}
                  </div>
                </div>
              </div>
            </div>

            <div className="border-t pt-6 flex flex-col md:flex-row gap-4">
              <div className="w-full md:max-w-xs">
                <Select
                  label={t("updateStatus")}
                  options={statusOptions}
                  value={newStatus}
                  onChange={setNewStatus}
                />
              </div>
              <Button
                onClick={handleStatusUpdate}
                disabled={newStatus === order.status}
                className="w-full md:w-auto"
              >
                {t("updateStatus")}
              </Button>
            </div>
          </div>
        );

      case "items":
        return (
          <div className="space-y-4 max-h-[50vh] overflow-y-auto">
            {order.products.map((item, i) => (
              <div
                key={i}
                className="flex flex-col gap-2 p-4 bg-gray-50 rounded-lg"
              >
                {/* صف المنتج الأساسي */}
                <div className="flex items-center gap-4">
                  <img
                    src={item.product.images[0]?.file_path}
                    className="w-14 h-14 object-cover rounded"
                  />
                  <div className="flex-1">
                    <div className="font-semibold">
                      {item.product.translations.ar.name}
                    </div>

                    {/* الكمية المطلوبة والوزن المختار */}
                    <div className="text-sm text-gray-500">
                      {t("quantity_label")}: {item.quantity} ×{" "}
                      {item.option.weight_string}
                    </div>
                  </div>

                 {/*  <div className="font-bold">
                    {formatPrice(order.total_price)}
                  </div>*/}
                {/* <div className="font-bold">
                    {formatPrice(item.option?.price * item.option?.quantity) }
                  </div>*/}
                </div>

                {/* كل الخيارات المتاحة */}
                <div className="mt-2">
                  <div className="text-sm font-medium text-gray-700 mb-1">
                    الخيارات المتاحة:
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {item.product.options.map((opt) => (
                      <div
                        key={opt.id}
                        className={`px-2 py-1 border rounded text-sm ${
                          opt.id === item.option.id
                            ? "bg-green-100 border-green-400 font-semibold"
                            : "bg-gray-100 border-gray-300"
                        }`}
                      >
                        {opt.weight_string} - {formatPrice(opt.price)} (متوفر:{" "}
                        {opt.quantity})
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        );

      case "shipping":
        return (
          <div>
            <div className="font-semibold mb-2">
              {order.first_name} {order.last_name}
            </div>
            <div>{order.address}</div>
            <div>{order.province.name}</div>
          </div>
        );

      case "payment":
        return (
          <div className="space-y-2">
            <div className="flex justify-between">
              <span>{t("method")}</span>
              <span>كاش عند الاستلام</span>
            </div>
            <div className="flex justify-between font-semibold">
              <span>{t("amount")}</span>
              <span>{formatPrice(order.total_price)}</span>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div
        className="
          bg-white
          rounded-xl
          shadow-lg
          w-full
          sm:w-[95%]
          md:w-[85%]
          lg:w-1/2
          max-w-3xl
          max-h-[85vh]
          flex flex-col
          overflow-hidden
        "
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-3">
            <Icon name="FileText" size={26} />
            <div>
              <h2 className="text-xl font-bold">{t("orderDetails")}</h2>
              <p className="text-sm text-gray-500">#{order.order_number}</p>
            </div>
          </div>
          <button onClick={onClose}>
            <Icon name="X" size={22} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 border-b-2 whitespace-nowrap ${
                activeTab === tab.id
                  ? "border-green-600 text-green-700 font-semibold"
                  : "border-transparent text-gray-500"
              }`}
            >
              <Icon name={tab.icon} size={16} />
              <span className="text-sm">{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">{renderTabContent()}</div>

        {/* Footer */}
        <div className="p-4 border-t flex justify-end">
          <Button variant="outline" onClick={onClose}>
            {t("close")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OrderDetailsModal;
